/**
 * The home's data, read at the boundary. Every field the backend added for the home
 * (owner, project, thumbnails, my last edit) may be missing from an older backend, so each
 * row is read loosely and anything that does not fit is treated as absent — never as a
 * crash, never as `NaN` on a card.
 */

export type Scope = 'recent' | 'mine' | 'shared' | 'workspace';

/** How many files one page of a list brings. */
export const PAGE = 50;

export interface Owner {
  readonly id: string;
  readonly name: string | null;
  readonly avatarUrl: string | null;
}

export interface FileRow {
  readonly id: string;
  readonly name: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  /** null: not in a project. */
  readonly projectId: string | null;
  /** null: the backend did not say. */
  readonly ownedByMe: boolean | null;
  readonly owner: Owner | null;
  readonly myLastEditAt: string | null;
  readonly thumbnailUrl: string | null;
  readonly thumbnailPending: boolean;
}

/** A project that holds Artwork files, as `artwork.doc.projects` answers it. */
export interface ProjectSummary {
  readonly projectId: string;
  readonly files: number;
  readonly updatedAt: string | null;
  /** Newest first, at most three. */
  readonly thumbnails: readonly string[];
  readonly owners: readonly Owner[];
}

/** A dashboard project, including projects with no Artwork files. null means counts are unavailable. */
export type ProjectOverview = Omit<ProjectSummary, 'files'> & { readonly files: number | null };

/** A project's name, colour and cover (projects-back's `project.list.forApp` card). */
export interface ProjectInfo {
  readonly id: string;
  readonly name: string;
  readonly color: string | null;
  /** The project's own cover picture, when it has one. */
  readonly cover?: string | null;
  /** The project's main brand (projects-back lists it first), for the badge on its folder. */
  readonly brandId?: string | null;
}

type Loose = Record<string, unknown>;

const isObject = (value: unknown): value is Loose => typeof value === 'object' && value !== null && !Array.isArray(value);
const text = (value: unknown): string | null => (typeof value === 'string' && value.trim() !== '' ? value : null);
const count = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.floor(value) : 0);

export function readOwner(raw: unknown): Owner | null {
  if (!isObject(raw)) return null;
  const id = text(raw.id);
  if (!id) return null;
  return { id, name: text(raw.name), avatarUrl: text(raw.avatarUrl) };
}

export function readRow(raw: unknown): FileRow | null {
  if (!isObject(raw)) return null;
  const id = text(raw.id);
  if (!id) return null;
  const updatedAt = text(raw.updatedAt) ?? text(raw.createdAt) ?? '';
  return {
    id,
    name: text(raw.name) ?? 'Untitled',
    createdAt: text(raw.createdAt) ?? updatedAt,
    updatedAt,
    projectId: text(raw.projectId),
    ownedByMe: typeof raw.ownedByMe === 'boolean' ? raw.ownedByMe : null,
    owner: readOwner(raw.owner),
    myLastEditAt: text(raw.myLastEditAt),
    thumbnailUrl: text(raw.thumbnailUrl),
    thumbnailPending: raw.thumbnailPending === true,
  };
}

export function readRows(raw: unknown): FileRow[] {
  return Array.isArray(raw) ? raw.map(readRow).filter((row): row is FileRow => row !== null) : [];
}

/** A file in the Trash or Archived (`artwork.doc.listTrashed`, `artwork.doc.listArchived`): a card, and when it went there. */
export interface PutAwayRow extends FileRow {
  /** When it went into the Trash; null in Archived. */
  readonly deletedAt: string | null;
  /** When the Trash deletes it for good (30 days on); null in Archived. */
  readonly purgeAt: string | null;
  readonly archivedAt: string | null;
}

export function readPutAwayRows(raw: unknown): PutAwayRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((one): PutAwayRow[] => {
    const row = readRow(one);
    if (!row || !isObject(one)) return [];
    return [{ ...row, deletedAt: text(one.deletedAt), purgeAt: text(one.purgeAt), archivedAt: text(one.archivedAt) }];
  });
}

/** How long a file has left in the Trash: "27 days left", "1 day left", "Less than a day left"; '' when unknown. */
export function timeLeft(purgeAt: string | null | undefined, now: Date = new Date()): string {
  const at = purgeAt ? Date.parse(purgeAt) : Number.NaN;
  if (Number.isNaN(at)) return '';
  const days = Math.floor((at - now.getTime()) / 86_400_000);
  if (days < 1) return 'Less than a day left';
  return days === 1 ? '1 day left' : `${days} days left`;
}

export function readSummaries(raw: unknown): ProjectSummary[] {
  if (!Array.isArray(raw)) return [];
  const out: ProjectSummary[] = [];
  for (const one of raw) {
    if (!isObject(one)) continue;
    const projectId = text(one.projectId);
    if (!projectId) continue;
    out.push({
      projectId,
      files: count(one.files),
      updatedAt: text(one.updatedAt),
      thumbnails: Array.isArray(one.thumbnails) ? one.thumbnails.map(text).filter((url): url is string => url !== null).slice(0, 3) : [],
      owners: Array.isArray(one.owners) ? one.owners.map(readOwner).filter((owner): owner is Owner => owner !== null) : [],
    });
  }
  return out;
}

/**
 * Every visible project belongs on Home's Projects row, even before its first Artwork file. Keep
 * projects with artwork first, newest first, then the rest by name. The project list (the shared
 * kit's `project.list.forApp`) is authoritative: summaries cannot bring back a hidden or
 * inaccessible project.
 */
export function namedSummaries(summaries: readonly ProjectSummary[] | null, infos: ReadonlyMap<string, ProjectInfo>): ProjectOverview[] {
  const byProject = new Map(summaries?.filter((one) => one.files > 0).map((one) => [one.projectId, one]));
  const updated = (one: ProjectOverview) => (one.updatedAt ? Date.parse(one.updatedAt) || 0 : 0);
  return [...infos.values()]
    .map((info): ProjectOverview => byProject.get(info.id) ?? {
      projectId: info.id,
      files: summaries === null ? null : 0,
      updatedAt: null,
      thumbnails: [],
      owners: [],
    })
    .sort((a, b) =>
      Number((b.files ?? 0) > 0) - Number((a.files ?? 0) > 0)
      || updated(b) - updated(a)
      || infos.get(a.projectId)!.name.localeCompare(infos.get(b.projectId)!.name)
      || a.projectId.localeCompare(b.projectId),
    );
}

/** A page appended to what is shown, without showing a file twice. */
export function appendPage<R extends { readonly id: string }>(shown: readonly R[], page: readonly R[]): R[] {
  const seen = new Set(shown.map((row) => row.id));
  return [...shown, ...page.filter((row) => !seen.has(row.id))];
}

export const anyPending = (rows: readonly FileRow[] | null | undefined): boolean => !!rows?.some((row) => row.thumbnailPending);

/**
 * "Just now", "12 min ago", "3 hr ago", "Yesterday", "4 days ago", then a date ("3 Sept",
 * with the year when it is not this one) — how long ago, the way Finder says it.
 */
export function relativeWhen(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const minutes = Math.floor((now.getTime() - then.getTime()) / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 24 * 60) return `${Math.floor(minutes / 60)} hr ago`;
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((day(now) - day(then)) / 86_400_000);
  if (days <= 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return then.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: then.getFullYear() === now.getFullYear() ? undefined : 'numeric' });
}

const FACE_COLOURS = ['#8b5cf6', '#ec4899', '#14b8a6', '#f59e0b', '#3b82f6', '#ef4444', '#10b981', '#6366f1'] as const;

/** A person's colour when they have no picture: the same one every time for the same id. */
export function colourFor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  return FACE_COLOURS[Math.abs(hash) % FACE_COLOURS.length]!;
}
